import { z } from 'zod';
import { DataPointTrace } from '../data-points/types';
import { IndicatorValueType } from '@upright/common/model/output/materiality/custom-company-input-data/computation/sustainability-indicators/types';

/**
 * Enum for Upright specific sustainability indicator IDs. Check the description
 * of each indicator below in the metadata dictionary.
 */
export enum DisclosureDataPointId {
  // WORKFORCE
  WF_EMPLOYEE_TURNOVER = 'wf-rate-of-employee-turnover',
  WF_TEMPORARY_EMPLOYEES = 'wf-share-of-temporary-employees',
  WF_NON_GUARANTEED_HOURS_EMPLOYEES = 'wf-non-guaranteed-hours-employees',
  WF_PART_TIME_EMPLOYEES = 'wf-part-time-employees',
  WF_FEMALE_EMPLOYEES = 'wf-total-number-of-female-employees',
  WF_NUMBER_OF_FATALITIES = 'wf-number-of-fatalities',
  WF_HIGH_CONSEQUENCE_WORK_RELATED_INJURIES = 'wf-rate-of-high-consequence-work-related-injuries',
  WF_RECORDABLE_WORK_RELATED_INJURIES = 'wf-rate-of-recordable-work-related-injuries',
  WF_WORK_RELATED_ILL_HEALTH = 'wf-number-of-cases-of-recordable-work-related-ill-health',
  WF_BOARD_GENDER_DIVERSITY = 'wf-board-gender-diversity',
  WF_GENDER_PAY_GAP = 'wf-gender-pay-gap',
  WF_CAREER_REVIEW = 'wf-career-review',
  WF_DISCRIMINATION = 'wf-discrimination',

  // BUSINESS CONDUCT
  BC_CORRUPTION = 'bc-corruption',
  BC_PRODUCT_LABELING = 'bc-product-labeling',
  BC_MARKETING = 'bc-marketing',
  BC_PRIVACY = 'bc-privacy',
  BC_DATA_BREACH = 'bc-data-breach',
  BC_HEALTH_SAFETY = 'bc-health-safety',

  // POLLUTION
  POLLUTION_SUBSTANCES_OF_VERY_HIGH_CONCERN = 'pollution-substances-of-very-high-concern',
}

const disclosureDataPointIds = Object.values(DisclosureDataPointId);

export function isDisclosureDataPointId(value: unknown): value is DisclosureDataPointId {
  return typeof value === 'string' && disclosureDataPointIds.includes(value as DisclosureDataPointId);
}

/**
 * Schema for a single sustainability indicator datapoint submission. Values outside the
 * indicator's range (see `getIndicatorValueBounds`) are rejected, so the API enforces the same
 * limits as the operational-data form.
 */
export const zSustainabilityDisclosureDataPointInput = z
  .object({
    disclosureId: z.nativeEnum(DisclosureDataPointId),
    value: z.number().finite(),
    year: z.number(),
    source: z.string().optional(),
    trace: z.nativeEnum(DataPointTrace),
  })
  .superRefine((dataPoint, ctx) => {
    const error = getIndicatorValueError(dataPoint.disclosureId, dataPoint.value);
    if (error) {
      const { name } = SUSTAINABILITY_INDICATOR_METADATA[dataPoint.disclosureId];
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['value'], message: `${name}: ${error}` });
    }
  });

export type SustainabilityDisclosureDataPointInput = z.infer<typeof zSustainabilityDisclosureDataPointInput>;

/**
 * Internal type with full metadata related to the datapoint
 */
export interface SustainabilityDisclosureDataPoint extends SustainabilityDisclosureDataPointInput {
  id: number;
  companyId: string;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface SustainabilityDisclosureDataPointWithCompanyName extends SustainabilityDisclosureDataPoint {
  companyName: string | null;
}

/**
 * A sustainability indicator determined e.g. by ESRS. See hardcoded objects below for the names and
 * descriptions of each indicator.
 */
export interface SustainabilityIndicator {
  id: DisclosureDataPointId;
  name: string;
  referenceCodes: string[]; // e.g., "GRI 401-1 b, ESRS S1 S1-6 §50"
  valueType: IndicatorValueType;
}

/**
 * Names and reference codes for each sustainability indicator
 */
export const SUSTAINABILITY_INDICATOR_METADATA: Record<DisclosureDataPointId, Omit<SustainabilityIndicator, 'id'>> = {
  [DisclosureDataPointId.WF_EMPLOYEE_TURNOVER]: {
    name: 'Rate of employee turnover during the reporting period',
    referenceCodes: ['GRI 401-1 b', 'ESRS S1 S1-6 §50'],
    valueType: 'percentage',
  },
  [DisclosureDataPointId.WF_TEMPORARY_EMPLOYEES]: {
    name: 'Share of temporary employees',
    referenceCodes: ['GRI 2-7 a', 'GRI 2-7 b ii', 'ESRS S1 S1-6 §50'],
    valueType: 'percentage',
  },
  [DisclosureDataPointId.WF_NON_GUARANTEED_HOURS_EMPLOYEES]: {
    name: 'Share of non-guaranteed hours employees',
    referenceCodes: ['GRI 2-7 a', 'GRI 2-7 b iii', 'ESRS S1 S1-6 §50'],
    valueType: 'percentage',
  },
  [DisclosureDataPointId.WF_PART_TIME_EMPLOYEES]: {
    name: 'Share of part-time employees',
    referenceCodes: ['GRI 2-7 a', 'GRI 2-7 b v', 'ESRS S1 S1-6 §52'],
    valueType: 'percentage',
  },
  [DisclosureDataPointId.WF_FEMALE_EMPLOYEES]: {
    name: 'Share of female employees',
    referenceCodes: ['GRI 2-7 a', 'ESRS S1 S1-6 §50'],
    valueType: 'percentage',
  },
  [DisclosureDataPointId.WF_NUMBER_OF_FATALITIES]: {
    name: 'All employees: Number of fatalities as a result of work-related injury',
    referenceCodes: ['GRI 403-9 a i', 'ESRS S1 S1-14 §88'],
    valueType: 'integer',
  },
  [DisclosureDataPointId.WF_HIGH_CONSEQUENCE_WORK_RELATED_INJURIES]: {
    name: 'All employees: Number of high-consequence work-related injuries (excluding fatalities) per million hours worked',
    referenceCodes: ['GRI 403-9 a ii', 'ESRS S1 S1-14 §88'],
    valueType: 'integer',
  },
  [DisclosureDataPointId.WF_RECORDABLE_WORK_RELATED_INJURIES]: {
    name: 'All employees: Number of recordable work-related injuries per million hours worked',
    referenceCodes: ['GRI 403-9 a iii', 'ESRS S1 S1-14 §88'],
    valueType: 'integer',
  },
  // NOTE: despite the enum key being `WF_WORK_RELATED_ILL_HEALTH` and the legacy
  // `DisclosureDataPointId` value being `wf-number-of-cases-of-recordable-work-related-ill-health`
  // (kept for stable IDs), this datapoint is the *share* (percentage) of employees
  // with recordable work-related ill-health cases — not a count. The GRI 403-10 a ii
  // source describes a count, but the platform asks for the percentage so the
  // disclosure-trigger threshold (defined as a `percentage` in the disclosure-trigger
  // GSheet) can be compared directly against the user's input. Keep `valueType` and
  // the sheet's `Threshold value type*` column aligned to `'percentage'`, and keep
  // the user-facing tooltip in `indicator-input.tsx` (`INDICATOR_TOOLTIPS`) in sync.
  [DisclosureDataPointId.WF_WORK_RELATED_ILL_HEALTH]: {
    name: 'All employees: Share of employees with cases of recordable work-related ill health',
    referenceCodes: ['GRI 403-10 a ii', 'ESRS S1 S1-14 §88'],
    valueType: 'percentage',
  },
  [DisclosureDataPointId.WF_BOARD_GENDER_DIVERSITY]: {
    name: 'Board gender diversity, expressed as a share of female members in the board of directors',
    referenceCodes: ['ESRS 2 GOV-1 §21'],
    valueType: 'percentage',
  },
  [DisclosureDataPointId.WF_GENDER_PAY_GAP]: {
    name: 'Unadjusted gender pay gap: ((mean hourly pay for men) - (mean hourly pay for women)) / (mean hourly pay for men) * 100',
    referenceCodes: ['ESRS S1 S1-16 §95'],
    valueType: 'percentage',
  },
  [DisclosureDataPointId.WF_CAREER_REVIEW]: {
    name: 'Share of total employees who received a regular performance and career development review during the reporting period',
    referenceCodes: ['GRI 404-3', 'ESRS S1 S1-13 §83 (a) and §84'],
    valueType: 'percentage',
  },
  [DisclosureDataPointId.WF_DISCRIMINATION]: {
    name: 'Total number of incidents of discrimination during the reporting period',
    referenceCodes: ['GRI 406-1 a', 'ESRS S1 S1-17 §103 (a)'],
    valueType: 'integer',
  },
  [DisclosureDataPointId.BC_CORRUPTION]: {
    name: 'Total number of confirmed incidents of corruption',
    referenceCodes: ['GRI 205-3', 'ESRS G1 G1-4 §25'],
    valueType: 'integer',
  },
  [DisclosureDataPointId.BC_PRODUCT_LABELING]: {
    name: 'Total number of incidents of non-compliance with regulations and/or voluntary codes concerning product and service information and labeling',
    referenceCodes: ['GRI 417-2 a', 'ESRS S4 S4-4 §35'],
    valueType: 'integer',
  },
  [DisclosureDataPointId.BC_MARKETING]: {
    name: 'Total number of incidents of non-compliance with regulations and/or voluntary codes concerning marketing communications, including advertising, promotion, and sponsorship',
    referenceCodes: ['GRI 417-3 a', 'ESRS S4 S4-4 §35'],
    valueType: 'integer',
  },
  [DisclosureDataPointId.BC_PRIVACY]: {
    name: 'Total number of substantiated complaints received concerning breaches of customer privacy',
    referenceCodes: ['GRI 418-1 a', 'ESRS S4 S4-4 §35'],
    valueType: 'integer',
  },
  [DisclosureDataPointId.BC_DATA_BREACH]: {
    name: 'Total number of identified leaks, thefts, or losses of customer data',
    referenceCodes: ['GRI 418-1 b', 'ESRS S4 S4-4 §35'],
    valueType: 'integer',
  },
  [DisclosureDataPointId.BC_HEALTH_SAFETY]: {
    name: 'Total number of incidents of non-compliance with regulations and/or voluntary codes concerning the health and safety impacts of products and services within the reporting period',
    referenceCodes: ['GRI 416-2 a', 'ESRS S4 S4-4 §35'],
    valueType: 'integer',
  },
  [DisclosureDataPointId.POLLUTION_SUBSTANCES_OF_VERY_HIGH_CONCERN]: {
    name: 'Total number of Substances of Very High Concern (SVHCs) identified in products and services',
    referenceCodes: ['ESRS E2 E2-5 § 32'],
    valueType: 'integer',
  },
};

export interface IndicatorValueBounds {
  /** Inclusive lower bound; absent means unbounded. */
  min?: number;
  /** Inclusive upper bound; absent means unbounded. */
  max?: number;
}

// Percentages are shares on a 0–100 scale; counts and per-million-hours rates can't be negative
// (but can be fractional, so integer-ness is deliberately not checked).
const DEFAULT_BOUNDS_BY_VALUE_TYPE: Record<IndicatorValueType, IndicatorValueBounds> = {
  percentage: { min: 0, max: 100 },
  integer: { min: 0 },
};

// Percentages that are not shares of a whole and so can leave 0–100.
const BOUNDS_OVERRIDES: Partial<Record<DisclosureDataPointId, IndicatorValueBounds>> = {
  // Leavers / average headcount: above 100% is real in high-churn sectors.
  [DisclosureDataPointId.WF_EMPLOYEE_TURNOVER]: { min: 0 },
  // A negative gap means women are paid more. The formula allows below -100 (women paid over twice
  // as much), but that is far likelier a lost decimal point. A negative gap deliberately fires no
  // disclosure trigger: regulators and ESG raters do not penalise it as they do a positive one
  // (PLT-7402), and the trigger compares the raw value against the `gt 5` trigger base.
  [DisclosureDataPointId.WF_GENDER_PAY_GAP]: { min: -100, max: 100 },
};

/** The allowed range of an indicator's value, shared by the API schema and the input form. */
export function getIndicatorValueBounds(id: DisclosureDataPointId): IndicatorValueBounds {
  return BOUNDS_OVERRIDES[id] ?? DEFAULT_BOUNDS_BY_VALUE_TYPE[SUSTAINABILITY_INDICATOR_METADATA[id].valueType];
}

/**
 * A human-readable reason the value is out of range, or `undefined` when it is allowed. It does
 * not name the indicator, so it reads as a field error; prefix the name where there is no field.
 */
export function getIndicatorValueError(id: DisclosureDataPointId, value: number): string | undefined {
  // Bounds alone do not reject NaN or positive infinity on indicators without a maximum.
  if (!Number.isFinite(value)) return 'Enter a finite number.';
  const { min, max } = getIndicatorValueBounds(id);
  const unit = SUSTAINABILITY_INDICATOR_METADATA[id].valueType === 'percentage' ? '%' : '';
  if (min !== undefined && value < min) {
    return `${value}${unit} is below the minimum of ${min}${unit}.`;
  }
  if (max !== undefined && value > max) {
    return `${value}${unit} is above the maximum of ${max}${unit}.`;
  }
  return undefined;
}

/**
 * Sustainability indicator category keys
 */
export enum SustainabilityIndicatorCategoryId {
  WORKFORCE = 'workforce',
  BUSINESS_CONDUCT = 'business-conduct',
  POLLUTION = 'pollution',
}

/**
 * A category to represent a group of indicators under shared themes
 */
export interface SustainabilityIndicatorCategory {
  id: SustainabilityIndicatorCategoryId;
  name: string;
  indicators: SustainabilityIndicator[];
}

/**
 * Categorization of sustainability indicators
 * Maps category keys to arrays of indicator IDs
 */
export const SUSTAINABILITY_INDICATOR_CATEGORIES: Record<string, DisclosureDataPointId[]> = {
  workforce: [
    DisclosureDataPointId.WF_EMPLOYEE_TURNOVER,
    DisclosureDataPointId.WF_TEMPORARY_EMPLOYEES,
    DisclosureDataPointId.WF_NON_GUARANTEED_HOURS_EMPLOYEES,
    DisclosureDataPointId.WF_PART_TIME_EMPLOYEES,
    DisclosureDataPointId.WF_FEMALE_EMPLOYEES,
    DisclosureDataPointId.WF_NUMBER_OF_FATALITIES,
    DisclosureDataPointId.WF_HIGH_CONSEQUENCE_WORK_RELATED_INJURIES,
    DisclosureDataPointId.WF_RECORDABLE_WORK_RELATED_INJURIES,
    DisclosureDataPointId.WF_WORK_RELATED_ILL_HEALTH,
    DisclosureDataPointId.WF_BOARD_GENDER_DIVERSITY,
    DisclosureDataPointId.WF_GENDER_PAY_GAP,
    DisclosureDataPointId.WF_CAREER_REVIEW,
    DisclosureDataPointId.WF_DISCRIMINATION,
  ],
  'business-conduct': [
    DisclosureDataPointId.BC_CORRUPTION,
    DisclosureDataPointId.BC_PRODUCT_LABELING,
    DisclosureDataPointId.BC_MARKETING,
    DisclosureDataPointId.BC_PRIVACY,
    DisclosureDataPointId.BC_DATA_BREACH,
    DisclosureDataPointId.BC_HEALTH_SAFETY,
  ],
  pollution: [DisclosureDataPointId.POLLUTION_SUBSTANCES_OF_VERY_HIGH_CONCERN],
};

/**
 * Names for each sustainability indicator category
 */
export const SUSTAINABILITY_CATEGORY_NAMES: Record<string, string> = {
  workforce: 'WORKFORCE',
  'business-conduct': 'BUSINESS CONDUCT & CUSTOMERS',
  pollution: 'POLLUTION',
};
